import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import * as IBooks from '../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../interfaces/merchants/resources/books/inputs';
import { Me } from '../../../../interfaces/users';

import scrollToTop from '../../../hoc/scrollToTop';

import {
  inputPayment,
  resetPaymentMethods,
  RestartReservationError,
} from '../../../../redux/actions/merchants/resources/books/inputs';
import { Dispatch } from '../../../../redux/reducers/merchants/resources/books/inputs';

import BooksSubmitButton from '../../../../components/books/common/buttons/BooksSubmitButton';
import BooksLabeledContainer from '../../../../components/books/model1/BooksLabeledContainer';
import InputPaymentCreditCard, {
  submittable as creditCardSubmittable,
} from './InputPaymentCreditCard';
import InputPaymentMyCreditCard from './InputPaymentMyCreditCard';
import InputPaymentMySubscription from './InputPaymentMySubscription';
import InputPaymentMyTicket from './InputPaymentMyTicket';
import InputPaymentPaymentMethods from './InputPaymentPaymentMethods';

interface ElementProps {
  modelType: IBooks.ModelType;
  settings: IBooks.SettingPayment;
  postable: boolean;
  onSubmit: () => void;
  onRestart: () => void;
}

interface StateProps {
  me: Me;
  inputs: IInputs.State;
}

interface DispatchProps {
  actions: {
    handleSubmit: (
      reservationId: number,
      payment: IInputs.Payment,
    ) => Promise<void>;
    handleResetPaymentMethods: (reservationId: number) => Promise<void>;
  };
}

interface State {
  paymentMethod?: IBooks.PaymentMethod;
  creditCard?: IBooks.CreditCard;
  cardSelection?: IBooks.CardSelection;
  subscriptionSelection?: string;
  ticketSelection?: string;
  control: Control;
}

interface Control {
  submittable?: boolean;
}

type Props = ElementProps & StateProps & DispatchProps & InjectedIntlProps;

const showableInputMyCreditCard = (
  me: Me,
  paymentMethod?: IBooks.PaymentMethod,
): boolean => {
  if (!paymentMethod) {
    return false;
  }
  if (paymentMethod.method !== 'credit_card') {
    return false;
  }
  if (!me.creditCard) {
    return false;
  }
  return true;
};

const showableInputMySubscription = (
  paymentMethod?: IBooks.PaymentMethod,
): boolean => {
  if (!paymentMethod) {
    return false;
  }
  if (paymentMethod.method !== 'subscription') {
    return false;
  }
  return true;
};

const showableInputMyTicket = (
  paymentMethod?: IBooks.PaymentMethod,
): boolean => {
  if (!paymentMethod) {
    return false;
  }
  if (paymentMethod.method !== 'ticket') {
    return false;
  }
  return true;
};

const showableInputCreditCard = (
  me: Me,
  paymentMethod?: IBooks.PaymentMethod,
  cardSelection?: IBooks.CardSelection,
): boolean => {
  if (!paymentMethod) {
    return false;
  }
  if (paymentMethod.method !== 'credit_card') {
    return false;
  }
  if (!me.creditCard) {
    return true;
  }
  if (!cardSelection) {
    return false;
  }
  return cardSelection === IBooks.CardSelection.Another;
};

const createSubmittable = (me: Me, state: State): boolean => {
  if (!state.paymentMethod) {
    return false;
  }
  if (
    state.paymentMethod.method === 'onsite' ||
    state.paymentMethod.method === 'bank_transfer'
  ) {
    return true;
  }
  if (state.paymentMethod.method === 'subscription') {
    return !!state.subscriptionSelection;
  }
  if (state.paymentMethod.method === 'ticket') {
    return !!state.ticketSelection;
  }
  if (!me.creditCard) {
    return !!state.creditCard && creditCardSubmittable(state.creditCard);
  }
  if (!state.cardSelection) {
    return false;
  }
  if (state.cardSelection === IBooks.CardSelection.Used) {
    return true;
  }
  return !!state.creditCard && creditCardSubmittable(state.creditCard);
};

const selectableProducts = (
  method: string,
  paymentMethods: IBooks.PaymentMethod[],
) => paymentMethods.filter(element => element.method === method)[0];

class InputPayment extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { control: { submittable: false } };
    this.handleSelectPaymentMethod = this.handleSelectPaymentMethod.bind(this);
    this.handleSelectSubscription = this.handleSelectSubscription.bind(this);
    this.handleSelectTicket = this.handleSelectTicket.bind(this);
    this.handleSelectCard = this.handleSelectCard.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleResetPaymentMethods = this.handleResetPaymentMethods.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.createControl = this.createControl.bind(this);

    this.state = this.createInitialState();
  }

  private createInitialState(): State {
    const { inputs } = this.props;
    const _state: State = {
      paymentMethod: inputs.payment ? inputs.payment.paymentMethod : undefined,
      creditCard: inputs.payment ? inputs.payment.creditCard : undefined,
      cardSelection: inputs.payment ? inputs.payment.cardSelection : undefined,
      control: {},
    };
    return { ..._state, control: this.createControl(_state) };
  }

  private handleSelectPaymentMethod(paymentMethod: IBooks.PaymentMethod) {
    const newState: State = {
      ...this.state,
      cardSelection: this.props.me.creditCard
        ? this.state.cardSelection || IBooks.CardSelection.Used
        : undefined,
      paymentMethod,
    };
    this.setState({ ...newState, control: this.createControl(newState) });
  }

  private handleSelectCard(cardSelection: IBooks.CardSelection) {
    const newState = { ...this.state, cardSelection };
    this.setState({ ...newState, control: this.createControl(newState) });
  }

  private handleSelectSubscription(subscriptionSelection: string) {
    const newState = { ...this.state, subscriptionSelection };
    this.setState({ ...newState, control: this.createControl(newState) });
  }

  private handleSelectTicket(ticketSelection: string) {
    const newState = { ...this.state, ticketSelection };
    this.setState({ ...newState, control: this.createControl(newState) });
  }

  private handleChange(creditCard: IBooks.CreditCard) {
    const newState = { ...this.state, creditCard };
    this.setState({ ...newState, control: this.createControl(newState) });
  }

  private createControl(state: State): Control {
    const { me } = this.props;
    return { submittable: createSubmittable(me, state) };
  }

  private async handleResetPaymentMethods() {
    const { inputs, onRestart } = this.props;
    try {
      await this.props.actions.handleResetPaymentMethods(inputs.reservationId!);
    } catch (e) {
      onRestart();
    }
  }

  private async handleSubmit() {
    const { inputs, onSubmit, onRestart } = this.props;
    const {
      paymentMethod,
      creditCard,
      cardSelection,
      subscriptionSelection,
      ticketSelection,
    } = this.state;
    try {
      await this.props.actions.handleSubmit(inputs.reservationId!, {
        cardSelection,
        subscriptionSelection,
        ticketSelection,
        creditCard,
        paymentMethod: paymentMethod!,
      });
      onSubmit();
    } catch (e) {
      if (e instanceof RestartReservationError) {
        onRestart();
      }
    }
  }

  public render() {
    const {
      settings: { paymentMethods, errors },
      inputs: { merchantId },
      postable,
      me,
      intl,
    } = this.props;
    const {
      paymentMethod,
      creditCard,
      cardSelection,
      subscriptionSelection,
      ticketSelection,
      control: { submittable },
    } = this.state;

    return (
      <div>
        <BooksLabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.pleaseSelectPaymentMethod',
          })}
          paddingTop={true}
        >
          <InputPaymentPaymentMethods
            paymentMethods={paymentMethods}
            selectedMethod={paymentMethod ? paymentMethod.method : undefined}
            onSelect={this.handleSelectPaymentMethod}
            subComponents={{
              credit_card: showableInputMyCreditCard(me, paymentMethod) && (
                <InputPaymentMyCreditCard
                  me={me}
                  onSelect={this.handleSelectCard}
                  mySelection={cardSelection}
                />
              ),
              onsite: null,
              bank_transfer: null,
              unspecified: null,
              subscription: showableInputMySubscription(paymentMethod) && (
                <InputPaymentMySubscription
                  merchantId={merchantId!}
                  paymentMethod={
                    selectableProducts(
                      'subscription',
                      paymentMethods,
                    ) as IBooks.PaymentMethodSubscription
                  }
                  onSelect={this.handleSelectSubscription}
                  onClose={this.handleResetPaymentMethods}
                  mySelection={subscriptionSelection}
                />
              ),
              ticket: showableInputMyTicket(paymentMethod) && (
                <InputPaymentMyTicket
                  merchantId={merchantId!}
                  paymentMethod={
                    selectableProducts(
                      'ticket',
                      paymentMethods,
                    ) as IBooks.PaymentMethodTicket
                  }
                  onSelect={this.handleSelectTicket}
                  onClose={this.handleResetPaymentMethods}
                  mySelection={ticketSelection}
                />
              ),
            }}
          />
        </BooksLabeledContainer>
        {showableInputCreditCard(me, paymentMethod, cardSelection) && (
          <InputPaymentCreditCard
            paymentMethod={paymentMethod}
            error={errors ? errors.creditCard : undefined}
            creditCard={creditCard || {}}
            onChange={this.handleChange}
          />
        )}
        <BooksSubmitButton
          type="button"
          onClick={this.handleSubmit}
          value={intl.formatMessage({
            id: 'books.submitLabel.confirm',
          })}
          disabled={!submittable || !postable}
          postable={postable}
        >
          {intl.formatMessage({
            id: 'books.submitLabel.confirm',
          })}
        </BooksSubmitButton>
      </div>
    );
  }
}

const mapStateProps = ({
  users: { me },
  merchantsResourcesBooksInputs,
}: {
  users: { me: Me };
  merchantsResourcesBooksInputs: IInputs.State;
}): StateProps => ({
  inputs: merchantsResourcesBooksInputs,
  me,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    handleSubmit: (reservationId: number, payment: IInputs.Payment) =>
      inputPayment(reservationId, payment)(dispatch),
    handleResetPaymentMethods: (reservationId: number) =>
      resetPaymentMethods(reservationId)(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(injectIntl(scrollToTop(InputPayment)));
